import { type MouseEventHandler, useCallback, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router";
import { hasAuthSession, readAuthSessionGeneration } from "../helpers/authSession";
import { loadDocumentAt, reloadCurrentDocument } from "../helpers/fullPageNavigation";
import { isDeploymentPending } from "../helpers/pendingDeployment";

// Loads the current deployment's document once this one is known to be stale.
//
// The load is deliberately deferred to a navigation. A navigation tears the
// current view down anyway, so replacing the document at that moment costs the
// user nothing they were not already losing, while an immediate reload would
// discard work in progress. The router has already committed the new URL by the
// time this runs, so the load lands on the page the user asked for.
//
// The one navigation that must be left alone is the redirect that completes a
// sign-in. Signing in issues the first request of the session, so it is also
// what first observes the new deployment and marks this document stale; the
// redirect that follows is then the very next navigation. Reloading there
// discards the session the user just established and returns them to the sign-in
// screen, where a second attempt succeeds only because a reloaded document
// starts unmarked. Two-factor accounts feel this twice, once per code.
//
// Skipping that one navigation costs nothing: the mark survives, and the next
// navigation inside the authenticated application loads the new document.
export default function useDeploymentReload(): MouseEventHandler<HTMLElement> {
  const location = useLocation();
  const previousKey = useRef(location.key);
  const replacementScheduled = useRef(false);
  const wasAuthenticated = useRef(hasAuthSession());
  const sessionGenerationAtMount = useRef(readAuthSessionGeneration());

  const loadPendingDeployment = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const authenticatedNow = hasAuthSession();
    if (!isDeploymentPending() || replacementScheduled.current || !authenticatedNow) return;
    // A link can also be the navigation that completes an authentication
    // flow. Preserve the one-navigation grace period described below.
    if (!wasAuthenticated.current && readAuthSessionGeneration() > sessionGenerationAtMount.current) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest<HTMLAnchorElement>("a[href]");
    if (!anchor || anchor.hasAttribute("download") || (anchor.target && anchor.target !== "_self")) return;
    if (anchor.getAttribute("href")?.trimStart().startsWith("#")) return;

    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== window.location.origin) return;

    event.preventDefault();
    replacementScheduled.current = true;
    loadDocumentAt(`${destination.pathname}${destination.search}${destination.hash}`);
  }, []);

  useLayoutEffect(() => {
    const navigated = previousKey.current !== location.key;
    previousKey.current = location.key;

    // Read after the navigation has committed: signing in populates the session
    // before it redirects, so this is true exactly on the redirect that
    // completes authentication.
    const authenticatedNow = hasAuthSession();
    // Only an explicit same-tab sign-in commit gets the grace navigation. A
    // session restored from an existing cookie does not emit that event and
    // must replace a stale document on its first user navigation.
    const justAuthenticated =
      !wasAuthenticated.current && authenticatedNow && readAuthSessionGeneration() > sessionGenerationAtMount.current;
    wasAuthenticated.current = authenticatedNow;
    if (justAuthenticated) sessionGenerationAtMount.current = readAuthSessionGeneration();

    if (!navigated) return;
    if (justAuthenticated) return;
    if (!isDeploymentPending()) return;
    if (replacementScheduled.current) return;
    replacementScheduled.current = true;
    // React Router commits the URL during the click task. Reloading from this
    // layout effect in the same task races that navigation in Firefox/WebKit:
    // they can abort the requested document and leave the public sign-in route
    // as the final URL. Let the route commit finish before replacing the stale
    // document.
    window.setTimeout(() => reloadCurrentDocument(), 0);
  }, [location.key]);

  return loadPendingDeployment;
}
